import * as React from 'react'
import { useNavigate, useParams } from 'react-router'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { createProposalInputSchema } from '@lib/input/proposal.input'
import { parseUuid } from '@lib/validators/uuid.validator'
import { useClient } from '@/hooks/useClient'
import { useProposalTemplate } from '@/hooks/useProposalTemplate'
import { useCreateProposal } from '@/hooks/useProposalMutations'
import { errorMessage } from '@/helpers/logger'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

/**
 * Start a proposal for a client.
 *
 * Sites are chosen here as checkboxes rather than a single select, because a
 * proposal can cover several — an HOA job across its parks is one proposal, and
 * making that the obvious default shape matters more than saving a control.
 *
 * Only live (non-archived) sites are offered: proposing work on a site someone
 * has retired is a mistake worth making impossible rather than catching later.
 * A client with exactly one starts with it chosen, since there is no other
 * answer.
 */
export function NewProposalPage() {
  const params = useParams<{ clientId: string }>()
  const clientId = parseUuid(params.clientId)
  const navigate = useNavigate()

  const client = useClient(clientId ?? '')
  const template = useProposalTemplate()
  const createProposal = useCreateProposal()

  const [title, setTitle] = React.useState<string>('')
  const [summary, setSummary] = React.useState<string>('')
  // Null until somebody touches the chips, so the one-site default can follow
  // the client once it loads instead of being fixed at the first render.
  const [chosenSiteIds, setChosenSiteIds] = React.useState<string[] | null>(
    null,
  )
  const [fieldError, setFieldError] = React.useState<string | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)

  if (clientId === null) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <button
          type="button"
          onClick={() => navigate('/clients')}
          className="inline-flex items-center gap-1.5 text-sm text-muted-text hover:text-primary-text"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Clients
        </button>
        <div className="rounded-lg border border-default bg-surface px-3 py-12 text-center">
          <p className="text-sm font-medium">Client not found</p>
          <p className="mt-1 text-sm text-muted-text">
            It may have been removed, or the link may be wrong.
          </p>
        </div>
      </div>
    )
  }

  const availableSites = (client.data?.sites ?? []).filter(
    (site) => site.archivedAt === null,
  )
  const onlySite = availableSites.length === 1 ? availableSites[0] : undefined
  const siteIds = chosenSiteIds ?? (onlySite ? [onlySite.id] : [])
  const narrative = template.data?.resolved.narrative

  function toggleSite(siteId: string): void {
    setChosenSiteIds(
      siteIds.includes(siteId)
        ? siteIds.filter((id) => id !== siteId)
        : [...siteIds, siteId],
    )
  }

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault()
    setFieldError(null)
    setFormError(null)

    const parsed = createProposalInputSchema.safeParse({
      clientId,
      title,
      summary,
      siteIds,
    })
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Check the details')
      return
    }

    try {
      const proposal = await createProposal.mutateAsync(parsed.data)
      navigate(`/proposals/${proposal.id}`, { replace: true })
    } catch (caught) {
      setFormError(errorMessage(caught, 'Could not create the proposal.'))
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <button
        type="button"
        onClick={() => navigate(`/clients/${clientId}`)}
        className="inline-flex items-center gap-1.5 text-sm text-muted-text hover:text-primary-text"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {client.data?.name ?? 'Client'}
      </button>

      <div>
        <h1 className="text-xl font-semibold">New proposal</h1>
        <p className="mt-1 text-sm text-muted-text">
          This creates version 1 as a draft. Nothing is sent until you send it.
        </p>
      </div>

      <form
        className="space-y-5 rounded-lg border border-default bg-surface p-4"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="space-y-1.5">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            value={title}
            placeholder="Tree maintenance — three phases"
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>

        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium text-secondary-text">
            Sites covered
          </legend>
          {availableSites.length === 0 ? (
            <p className="pt-1 text-xs text-muted-text">
              This client has no sites yet. You can add them later and attach
              them to a version.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 pt-1">
              {availableSites.map((site) => {
                const selected = siteIds.includes(site.id)
                return (
                  <button
                    key={site.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleSite(site.id)}
                    className={cn(
                      'rounded-md border px-2.5 py-1.5 text-sm transition-colors',
                      selected
                        ? 'border-brand bg-brand-subtle font-medium text-primary-text'
                        : 'border-default text-secondary-text hover:border-strong hover:bg-subtle',
                    )}
                  >
                    {site.name}
                  </button>
                )
              })}
            </div>
          )}
          {/*
            The chips start unselected and look like labels, which is how a
            proposal comes to be created covering nothing. A proposal with no
            site cannot be sent, so this says so before it is made.
          */}
          {availableSites.length > 0 && siteIds.length === 0 ? (
            <p className="pt-1 text-xs text-warning">
              Choose at least one site. A proposal can&apos;t be sent without
              one.
            </p>
          ) : null}
        </fieldset>

        {/*
          Named for the heading it prints under. This is the one paragraph
          written for this community, and calling it a "description" made it
          read like a cover letter, which is not what it becomes.
        */}
        <div className="space-y-1.5">
          <Label htmlFor="summary">
            {narrative?.title ?? 'Why this work, and why now'}
          </Label>
          <textarea
            id="summary"
            rows={5}
            value={summary}
            placeholder={narrative?.paragraphs.join('\n\n') ?? ''}
            onChange={(event) => setSummary(event.target.value)}
            className="flex w-full rounded-md border border-default bg-surface px-2.5 py-1.5 text-sm text-primary-text transition-colors placeholder:text-disabled-text hover:border-strong focus-visible:outline-none"
          />
          <p className="text-xs text-muted-text">
            Optional, and easier once the survey is done. Blank uses your
            standard text, shown in gray.
          </p>
        </div>

        {fieldError ? (
          <p className="text-sm text-destructive">{fieldError}</p>
        ) : null}
        {formError ? (
          <p className="text-sm text-destructive">{formError}</p>
        ) : null}

        <div className="flex items-center gap-2 border-t border-default pt-4">
          <Button type="submit" disabled={createProposal.isPending}>
            {createProposal.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            Create proposal
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(`/clients/${clientId}`)}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  )
}
